import { Edit } from "@refinedev/mui";
import {
  Box,
  TextField,
  FormControlLabel,
  Checkbox,
  Grid,
  Card,
  CardContent,
} from "@mui/material";
import { useForm } from "@refinedev/react-hook-form";
import {
  IResourceComponentsProps,
  useTranslate,
  useApiUrl,
} from "@refinedev/core";
import { Controller } from "react-hook-form";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format } from "date-fns";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getToken } from "../../utils/auth-storage";
import axiosInstance from "../../api/axios-config";


export const PromocodeEdit: React.FC<IResourceComponentsProps> = () => {
  const translate = useTranslate();
  const apiUrl = useApiUrl();
  const params = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    saveButtonProps,
    refineCore: { formLoading, queryResult },
    register,
    control,
    formState: { errors },
    setValue,
    getValues,
    watch,
  } = useForm();

  const promocodeData = queryResult?.data?.data;

  // Log the data received from the API to debug
  useEffect(() => {
    if (promocodeData) {

      // Initialize the date value from promocodeData if available
      if (promocodeData.expiresAt) {
        const parsedDate = new Date(promocodeData.expiresAt);

        setValue("expiresAt", parsedDate);
      }
    }
  }, [promocodeData, setValue]);

  // Watch the expiresAt value to see changes
  const expiresAtValue = watch("expiresAt");


  // Custom save handler using PUT instead of PATCH
  const handleSaveClick = async (e: React.BaseSyntheticEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formValues = getValues();

      // Format the date correctly if needed
      if (formValues.expiresAt instanceof Date) {
        formValues.expiresAt = formValues.expiresAt.toISOString();
      }

      // Get API URL
      const baseApiUrl = apiUrl;

      // Get auth token
      const authToken = getToken();
      if (!authToken) {
        console.error("No auth token found");
        setIsSubmitting(false);
        return;
      }


      // Make direct API call with PUT method
      const response = await axiosInstance.put(
        `${baseApiUrl}/promo-codes/${params?.id}`,
        formValues,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
        }
      );


      window.location.href = "/promo-codes";
    } catch (error) {
      console.error("Error updating promocode:", error);
      alert(
        `Failed to update promocode: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Edit
      isLoading={formLoading || isSubmitting}
      saveButtonProps={{
        onClick: handleSaveClick,
        disabled: isSubmitting,
      }}
    >
      <Box
        component="form"
        sx={{ display: "flex", flexDirection: "column" }}
        autoComplete="off"
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <TextField
                  {...register("code", {
                    required: "This field is required",
                  })}
                  error={!!(errors as any)?.code}
                  helperText={(errors as any)?.code?.message}
                  margin="normal"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    sx: {
                      fontFamily: "'Courier New', Courier, monospace",
                      fontSize: "1.1rem",
                      letterSpacing: "0.5px",
                      "& input": {
                        textTransform: "uppercase",
                      },
                    },
                  }}
                  type="text"
                  label="Promocode"
                  name="code"
                  defaultValue={promocodeData?.code}
                />

                <TextField
                  {...register("duration", {
                    required: "This field is required",
                    valueAsNumber: true,
                  })}
                  error={!!(errors as any)?.duration}
                  helperText={(errors as any)?.duration?.message}
                  margin="normal"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  type="number"
                  label="Duration (days)"
                  name="duration"
                  defaultValue={promocodeData?.duration}
                />

                <TextField
                  {...register("maxUses", {
                    required: "This field is required",
                    valueAsNumber: true,
                  })}
                  error={!!(errors as any)?.maxUses}
                  helperText={(errors as any)?.maxUses?.message}
                  margin="normal"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  type="number"
                  label="Max Uses"
                  name="maxUses"
                  defaultValue={promocodeData?.maxUses}
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Controller
                  control={control}
                  name="expiresAt"
                  defaultValue={
                    promocodeData?.expiresAt
                      ? new Date(promocodeData?.expiresAt)
                      : null
                  }
                  rules={{ required: "This field is required" }}
                  render={({ field }) => (
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        label="Expires At"
                        value={field.value}
                        onChange={(date) => {
                          field.onChange(date);
                        }}
                        format="dd/MM/yyyy"
                        slotProps={{
                          textField: {
                            margin: "normal",
                            fullWidth: true,
                            error: !!(errors as any)?.expiresAt,
                            helperText: (errors as any)?.expiresAt?.message,
                            InputLabelProps: { shrink: true },
                            variant: "outlined",
                          },
                        }}
                        minDate={new Date()}
                      />
                    </LocalizationProvider>
                  )}
                />

                <Controller
                  control={control}
                  name="isActive"
                  defaultValue={promocodeData?.isActive ?? true}
                  render={({ field }) => (
                    <FormControlLabel
                      label="Active"
                      sx={{ marginTop: 2, display: "block" }}
                      control={
                        <Checkbox
                          {...field}
                          checked={field.value}
                          onChange={(event) => {
                            field.onChange(event.target.checked);
                          }}
                        />
                      }
                    />
                  )}
                />

                {/* Display current used count (read-only) */}
                <TextField
                  margin="normal"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  type="number"
                  label="Used Count"
                  name="usedCount"
                  value={promocodeData?.usedCount || 0}
                  disabled
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Edit>
  );
};
